import { ClearIndicator } from 'components/shared/Tags/clear-indicator';
import { TagsPicker } from 'components/shared/Tags/tags-picker';
import { SearchIcon } from 'components/shared/underlyings/selector/search-icon';
import { SingleValue } from 'components/shared/underlyings/selector/single/single-value';
import { useUnderlyingsTags } from 'hooks/underlyings/use-underlyings-tags';
import { useUserNotification } from 'hooks/useUserNotification';
import type { Underlying } from 'models/underlying';
import type { UnderlyingCodeType } from 'models/underlying/base';
import { UnderlyingSearch } from 'models/underlying/underlying-search';
import { useCallback } from 'react';

import * as menuList from '../result/menu-list';
import * as Menu from '../result/no-option-message';
import * as option from '../result/option';

interface Props extends UnderlyingSearch {
  id: string;

  underlying?: Underlying;
  onChange: (underlying?: Underlying) => void;
  placeholder?: string;
  defaultUnderlyingCodeType: UnderlyingCodeType;
  hasErrors?: boolean;
  tabIndex?: number;
}
export function SingleUnderlyingSelector({
  id,
  underlying,
  onChange,
  placeholder,
  hasErrors,
  defaultUnderlyingCodeType,
  tabIndex,
  getOptionsFromPastedLabels,
  search,
}: Props) {
  const { tags, onTagsChanged } = useUnderlyingsTags({
    underlying,
    isMulti: false,
    onChange,
    removeInvalidTagsOnUnderlyingsChange: false,
    codeType: defaultUnderlyingCodeType,
    underlyingsSyncMode: 'keep_different',
  });
  const { warn } = useUserNotification();
  const convertPastedLabelsToTags = useCallback(
    (pastedLabels: string[]) => {
      if (pastedLabels.length > 1) {
        warn({
          title: 'Single underlying',
          content: ["You can't paste multiple underlyings"],
        });
        pastedLabels = [];
      }
      return getOptionsFromPastedLabels(pastedLabels, [], defaultUnderlyingCodeType);
    },
    [getOptionsFromPastedLabels, defaultUnderlyingCodeType, warn],
  );
  return (
    <div className="input-group">
      <SearchIcon />
      <TagsPicker<Underlying>
        autoFocus
        components={{
          MenuList: menuList.MenuList,
          Option: option.Option,
          NoOptionsMessage: Menu.NoOptionsMessage,
          SingleValue: p => <SingleValue {...p} canHighlight={false} onTagRemoved={() => onTagsChanged([], true)} />,
          ClearIndicator: p => <ClearIndicator.Default {...p} tabIndex={tabIndex} />,
        }}
        classNames={{ singleValue: () => 'single-value' }}
        convertPastedLabelsToTags={convertPastedLabelsToTags}
        filterTags={input => search(input, [], [], defaultUnderlyingCodeType)}
        id={id}
        isMulti={false}
        onTagsChanged={t => {
          onTagsChanged(t ? [t] : [], true);
        }}
        placeholder={placeholder}
        tags={tags}
        hasErrors={hasErrors}
        tabIndex={tabIndex}
      />
    </div>
  );
}
